import { Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { cssVars } from "../../../statistics/components/resourceDashboard/resourceDashboardTypes"

import type { HourSlot } from "./guestsOverviewLib"

const useStyles = makeStyles()((theme) => ({
    caption: {
        color: theme.palette.tailwind.slate[400],
    },
    legendItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        color: theme.palette.tailwind.slate[400],
    },
    swatch: {
        width: ".625rem",
        height: ".625rem",
        borderRadius: ".25rem",
    },
    meetingsColor: {
        backgroundColor: theme.palette.tailwind.teal[500],
    },
    guestsColor: {
        backgroundColor: theme.palette.tailwind.teal[200],
    },
    chart: {
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: ".625rem",
    },
    column: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: ".25rem",
    },
    total: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.tailwind.slate[900],
    },
    bars: {
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: ".25rem",
        width: "100%",
        height: "4.5rem",
    },
    bar: {
        width: "min(.875rem, 40%)",
        height: "var(--bar-height)",
        borderRadius: ".375rem .375rem 0 0",
    },
    hour: {
        color: theme.palette.tailwind.slate[500],
    },
    totalHidden: {
        visibility: "hidden",
    },
    emptyBar: {
        width: "1.5rem",
        height: ".125rem",
        borderRadius: ".125rem",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    emptyState: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        height: "7.5rem",
    },
    emptyLine: {
        flex: 1,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    emptyText: {
        color: theme.palette.tailwind.slate[400],
        whiteSpace: "nowrap",
    },
    skeleton: {
        height: "6.5rem",
        borderRadius: ".75rem",
    },
}))

type Props = Readonly<{
    slots: ReadonlyArray<HourSlot>
    isLoading: boolean
}>

const toHourLabel = (hour: number) => String(hour).padStart(2, "0")

const isEmptySlot = (slot: HourSlot) => slot.meetings + slot.guests === 0

export function TodayActivityChart({ slots, isLoading }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    const maxValue = Math.max(1, ...slots.flatMap((slot) => [slot.meetings, slot.guests]))
    const isEmptyDay = slots.every(isEmptySlot)
    const toBarHeight = (value: number) => `${Math.round((value / maxValue) * 100)}%`

    return (
        <Stack gap=".5rem" width="100%">
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap="1rem">
                <LocalizedStrict id="service-center-reports-dashboard-guests-overview-chart-title">
                    <Typography variant="body2" className={classes.caption}>
                        Today
                    </Typography>
                </LocalizedStrict>
                <Stack direction="row" gap="1rem">
                    <Box className={classes.legendItem}>
                        <span className={cx(classes.swatch, classes.meetingsColor)} />
                        <LocalizedStrict id="service-center-reports-dashboard-guests-overview-chart-legend-meetings">
                            <Typography variant="body2" component="span">
                                Meetings
                            </Typography>
                        </LocalizedStrict>
                    </Box>
                    <Box className={classes.legendItem}>
                        <span className={cx(classes.swatch, classes.guestsColor)} />
                        <LocalizedStrict id="service-center-reports-dashboard-guests-overview-chart-legend-guests">
                            <Typography variant="body2" component="span">
                                Guests
                            </Typography>
                        </LocalizedStrict>
                    </Box>
                </Stack>
            </Stack>

            {isLoading ? (
                <Skeleton variant="rectangular" className={classes.skeleton} />
            ) : isEmptyDay ? (
                <Box className={classes.emptyState}>
                    <span className={classes.emptyLine} />
                    <LocalizedStrict id="service-center-reports-dashboard-guests-overview-chart-empty">
                        <Typography variant="body2" className={classes.emptyText}>
                            No meetings today
                        </Typography>
                    </LocalizedStrict>
                    <span className={classes.emptyLine} />
                </Box>
            ) : (
                <Box className={classes.chart}>
                    {slots.map((slot) => (
                        <Tooltip
                            key={slot.hour}
                            title={l10n.getString(
                                "service-center-reports-dashboard-guests-overview-chart-slot-tooltip",
                                {
                                    from: toHourLabel(slot.hour),
                                    to: toHourLabel(slot.hour + 1),
                                    meetings: slot.meetings,
                                    guests: slot.guests,
                                },
                            )}
                        >
                            <Box className={classes.column}>
                                <Typography
                                    variant="body2"
                                    className={cx(classes.total, isEmptySlot(slot) && classes.totalHidden)}
                                >
                                    {slot.meetings + slot.guests}
                                </Typography>
                                <Box className={classes.bars}>
                                    {isEmptySlot(slot) ? (
                                        <Box className={classes.emptyBar} />
                                    ) : (
                                        <>
                                            <Box
                                                className={cx(classes.bar, classes.meetingsColor)}
                                                style={cssVars({ "--bar-height": toBarHeight(slot.meetings) })}
                                            />
                                            <Box
                                                className={cx(classes.bar, classes.guestsColor)}
                                                style={cssVars({ "--bar-height": toBarHeight(slot.guests) })}
                                            />
                                        </>
                                    )}
                                </Box>
                                <Typography variant="body2" className={classes.hour}>
                                    {toHourLabel(slot.hour)}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            )}
        </Stack>
    )
}
